import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { QUICK_LINK_QUERY_KEYS } from './keys';

import {
  addQuickLinks,
  addQuickLinksCategory,
  deleteQuickLinks,
  deleteQuickLinksCategory,
  fetchQuickLinks,
  updateLinkLibraryWeights,
  updateQuickLinks,
  updateQuickLinksCategory,
} from 'services/quickLinks';
import type { QuickLinkCategoryPayload, QuickLinkPayload } from 'interface/link';
import { handleError } from 'utils/errorHandler';

import type { ApiEnvelope, ServiceId, ServicePayload } from 'interface/service';
import { LinkLibraryTreeNode } from 'interface/linkLibraryTree';

type QuickLinksItem = LinkLibraryTreeNode & { weight: number };
type QuickLinksData = QuickLinksItem[];
type QueryError = Error;

export const QUICK_LINKS_QUERY_KEY = [QUICK_LINK_QUERY_KEYS.QUICK_LINKS] as const;

const fetchQuickLinksData = async <TData>(): Promise<TData> => {
  return (await fetchQuickLinks()) as TData;
};

type UseFetchQuickLinksResult<TData> = UseQueryResult<TData, QueryError>;

type UseUpdateLinkLibraryMutationResult = {
  mutate: UseMutationResult<ApiEnvelope, QueryError, ServicePayload, void>['mutate'];
  isLoading: boolean;
};

export const useFetchQuickLinks = <TData = QuickLinksData>(): UseFetchQuickLinksResult<TData> => {
  return useQuery<TData, QueryError>({
    queryKey: QUICK_LINKS_QUERY_KEY,
    queryFn: () => fetchQuickLinksData<TData>(),
  });
};

const useInvalidateQuickLinks = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: QUICK_LINKS_QUERY_KEY });
};

export const useUpdateLinkLibraryMutation = (): UseUpdateLinkLibraryMutationResult => {
  const invalidateQuickLinks = useInvalidateQuickLinks();
  const { mutate, isPending } = useMutation<ApiEnvelope, QueryError, ServicePayload>({
    mutationFn: updateLinkLibraryWeights,
    onError: (error) => handleError(error),
    onSuccess: invalidateQuickLinks,
  });

  return {
    mutate,
    isLoading: isPending,
  };
};

export const useAddQuickLinksCategoryMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: (category: QuickLinkCategoryPayload) => addQuickLinksCategory(category),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};

export const useUpdateQuickLinksCategoryMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: ({ id, category }: { id: ServiceId; category: QuickLinkCategoryPayload }) =>
      updateQuickLinksCategory(id, category),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};

export const useDeleteQuickLinksCategoryMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: (id: ServiceId) => deleteQuickLinksCategory(id),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};

export const useAddQuickLinksMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: (link: QuickLinkPayload) => addQuickLinks(link),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};

export const useUpdateQuickLinksMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: ({ id, link }: { id: ServiceId; link: QuickLinkPayload }) => updateQuickLinks(id, link),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};

export const useDeleteQuickLinksMutation = () => {
  const invalidateQuickLinks = useInvalidateQuickLinks();

  return useMutation({
    mutationFn: (id: ServiceId) => deleteQuickLinks(id),
    onSuccess: invalidateQuickLinks,
    onError: (error) => handleError(error),
  });
};
